/**
 * Tools & Resources Index Page for ShopKaBot
 */

import { Box, Container, Grid, Typography, Card, CardContent, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import Seo from "../../seo/Seo";
import { createOrganizationSchema, createWebsiteSchema, createBreadcrumbSchema } from "../../seo/utils";
import toolsData from "../../data/tools.json";

interface Tool {
  id: number;
  slug: string;
  title: string;
  description: string;
  category: string;
  icon: string;
  color: string;
  path: string;
  shortDescription: string;
}

function ToolsPage() {
  const navigate = useNavigate();
  const tools: Tool[] = toolsData.tools;

  const schema = [
    createOrganizationSchema(),
    createWebsiteSchema(),
    createBreadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Tools & Resources", path: "/tools" },
    ]),
  ];

  return (
    <>
      <Seo
        title="Free Tools & Resources - ShopKaBot"
        description="Access free AI automation tools, calculators, guides, and resources for WhatsApp business automation."
        path="/tools"
        keywords={["automation tools", "ROI calculator", "chatbot", "WhatsApp automation", "free tools"]}
        schema={schema}
        type="website"
      />

      <Box sx={{ py: { xs: 4, md: 8 }, backgroundColor: "#f8f9fa" }}>
        <Container maxWidth="lg">
          {/* Header */}
          <Box sx={{ textAlign: "center", mb: 6 }}>
            <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 2 }}>
              Free Tools & Resources
            </Typography>
            <Typography variant="h6" color="textSecondary" sx={{ mb: 4, maxWidth: "600px", mx: "auto" }}>
              Explore our collection of free tools designed to help you automate your business, calculate ROI, and implement AI agents effectively.
            </Typography>
          </Box>

          {/* Tools Grid */}
          <Grid container spacing={3}>
            {tools.map((tool) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={tool.id}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: 6,
                    },
                  }}
                >
                  <Box
                    sx={{
                      height: 120,
                      background: `linear-gradient(135deg, ${tool.color}22 0%, ${tool.color}11 100%)`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "48px",
                      borderBottom: `2px solid ${tool.color}`,
                    }}
                  >
                    {tool.icon === "calculator" && "🧮"}
                    {tool.icon === "book" && "📚"}
                    {tool.icon === "zap" && "⚡"}
                    {tool.icon === "target" && "🎯"}
                    {tool.icon === "users" && "👥"}
                    {tool.icon === "mail" && "✉️"}
                  </Box>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="overline" color="textSecondary" sx={{ mb: 0.5 }}>
                      {tool.category}
                    </Typography>
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                      {tool.title}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {tool.shortDescription}
                    </Typography>
                  </CardContent>
                  <Box sx={{ p: 2, pt: 0 }}>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={() => navigate(tool.path)}
                    >
                      Access Tool
                    </Button>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* CTA Section */}
          <Box
            sx={{
              mt: 8,
              p: 4,
              textAlign: "center",
              backgroundColor: "white",
              borderRadius: 2,
              border: "1px solid rgba(59, 130, 246, 0.1)",
            }}
          >
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
              Need a Custom Solution?
            </Typography>
            <Typography variant="body1" color="textSecondary" sx={{ mb: 3, maxWidth: "600px", mx: "auto" }}>
              Our experts can help you design a custom AI automation strategy tailored to your specific business needs.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate("/contact-us")}
            >
              Fill Lead Form
            </Button>
          </Box>
        </Container>
      </Box>
    </>
  );
}

export default ToolsPage;
